import type { Favorite, Side } from '../constants';

/**
 * Spread representation.
 *
 * The database stores exactly one number per contest: `contest_spread_home`, the *home*
 * team's line. Negative means the home team is favored (`KSU -4.5`), positive means the
 * home team is the underdog (`KSU +3.0`), zero is a pick'em.
 *
 * Nothing user-facing ever shows a raw signed database value. Every screen asks this
 * module for the line belonging to the side it is rendering, already signed and formatted
 * from that side's point of view — which is how people actually talk about spreads:
 *
 *     Oklahoma State +4.5   at   Kansas State -4.5
 */

/** Lines are always multiples of a half point. */
export function isValidSpread(spread: number): boolean {
  return Number.isFinite(spread) && Math.abs(spread * 2 - Math.round(spread * 2)) < 1e-9;
}

/**
 * Snap a loose input to the nearest half point — a commissioner's typed value, or a
 * consensus that landed between two books.
 *
 * Rounds half AWAY FROM ZERO rather than using `Math.round`, which breaks ties toward
 * +Infinity: `Math.round(-6.5)` is `-6` while `Math.round(6.5)` is `7`. On spreads that
 * means an identical quarter-point split resolves in opposite directions depending on
 * which side happens to be favored — a systematic bias in the one number the entire
 * league is graded against.
 */
export function normalizeSpread(spread: number): number {
  const doubled = spread * 2;
  const rounded = doubled < 0 ? -Math.round(-doubled) : Math.round(doubled);
  // `|| 0` collapses -0, which would otherwise render as "-0" instead of a pick'em.
  return rounded / 2 || 0;
}

/**
 * Is this a line that cannot end in a push?
 *
 * True only for odd half points — `-7.5`, `+3.5`, `-0.5`. A whole number can be landed on
 * exactly, and a whole-number line that the final margin matches is a push.
 */
export function isDecisiveSpread(spread: number): boolean {
  return isValidSpread(spread) && Math.abs(Math.round(spread * 2)) % 2 === 1;
}

/**
 * Snap a line to one that cannot push.
 *
 * This league does not want pushes, so every contest spread carries a hook. A line that is
 * already a half point is left exactly as the market set it; only whole numbers move, and
 * they move by exactly half a point.
 *
 * WHICH WAY THE HOOK GOES
 * There is no unbiased direction — half a point has to come from somewhere, and whichever
 * way it moves, one side of that game is now slightly harder to back. So the rule is fixed
 * and stated rather than chosen per game: **the hook always goes against the favorite.**
 * `-7` becomes `-7.5`, so a seven-point win no longer covers; `+7` becomes `+7.5`, which is
 * the same rule seen from the other side, because there the away team is the favorite.
 *
 * Applying it consistently is what makes it fair. Every player can see the line before
 * picking and can take either side of it, so a systematic tilt toward underdogs is a known
 * feature of the pool rather than an advantage to anybody in it. Choosing per game — say,
 * rounding toward whichever number looked "closer" — would be the unfair version, because
 * then the rule depends on data nobody can see.
 *
 * A pick'em has no favorite to charge, so it resolves to `-0.5`: the home team must win
 * outright. That is what a pick'em already means in practice, and the only case it decides
 * differently is an actual tie, which college football cannot produce at all and the NFL
 * manages roughly once a season.
 */
export function toDecisiveSpread(spread: number): number {
  const snapped = normalizeSpread(spread);
  if (isDecisiveSpread(snapped)) return snapped;
  // A true pick'em: nominate the home side, so "cover" means "win the game".
  if (snapped === 0) return -0.5;
  // Away from zero — which is the favorite's side of the number, whichever team that is.
  return snapped < 0 ? snapped - 0.5 : snapped + 0.5;
}

/** The line for one side, given the home team's line. */
export function lineForSide(contestSpreadHome: number, side: Side): number {
  return side === 'home' ? contestSpreadHome : -contestSpreadHome;
}

/**
 * Format a line the way a bettor reads it: `-4.5`, `+3`, `PK`.
 * Trailing `.0` is dropped so `-3.0` renders as `-3`, which is how it is spoken.
 */
export function formatLine(line: number): string {
  if (Math.abs(line) < 1e-9) return 'PK';
  const abs = Math.abs(line);
  const body = abs % 1 === 0 ? String(abs) : abs.toFixed(1);
  return `${line < 0 ? '-' : '+'}${body}`;
}

/** Format the line belonging to one side of a contest. */
export function formatSideLine(contestSpreadHome: number, side: Side): string {
  return formatLine(lineForSide(contestSpreadHome, side));
}

/** Which side the sportsbook made the favorite, derived from the stored home line. */
export function favoriteFromSpread(contestSpreadHome: number): Favorite {
  if (Math.abs(contestSpreadHome) < 1e-9) return 'even';
  return contestSpreadHome < 0 ? 'home' : 'away';
}

/** `true` when this side is laying points rather than taking them. */
export function isFavorite(contestSpreadHome: number, side: Side): boolean {
  return lineForSide(contestSpreadHome, side) < 0;
}

/**
 * One-line matchup summary, e.g. `Oklahoma State +4.5 at Kansas State`.
 * `neutralSite` swaps `at` for `vs` — a bowl game or a neutral-site kickoff classic has
 * no true home team even though the schedule assigns one.
 */
export function describeMatchup(args: {
  awayName: string;
  homeName: string;
  contestSpreadHome: number;
  neutralSite?: boolean;
}): string {
  const { awayName, homeName, contestSpreadHome, neutralSite } = args;
  const joiner = neutralSite ? 'vs' : 'at';
  return `${awayName} ${formatSideLine(contestSpreadHome, 'away')} ${joiner} ${homeName}`;
}

/**
 * Human phrasing of a spread for confirmation copy and screen-reader labels.
 * e.g. "Oklahoma State to lose by 4 or fewer, or win outright".
 */
export function describeSideCondition(args: {
  teamName: string;
  contestSpreadHome: number;
  side: Side;
}): string {
  const line = lineForSide(args.contestSpreadHome, args.side);
  if (Math.abs(line) < 1e-9) return `${args.teamName} to win outright`;
  if (line < 0) {
    const by = Math.abs(line);
    return `${args.teamName} to win by more than ${formatPoints(by)}`;
  }
  return `${args.teamName} to lose by fewer than ${formatPoints(line)}, or win outright`;
}

function formatPoints(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}
